/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormContext } from '@jalik/react-form'
import { upperFirst } from 'lodash'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import FormField from '../../../../client/js/components/form/fields/FormField'
import InterventionSelectorModal from '../intervention/InterventionSelectorModal'
import { getTransactionLink } from '../../lib/links/transactionLinks'
import useTransactionCategoryOptions from '../transaction/useTransactionCategoryOptions'
import { parseBoolean, parseNumber } from '@jalik/form-parser'
import { ActionIcon, Checkbox, Group, Select, Table, Tooltip } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle'
import { faFolderOpen } from '@fortawesome/free-solid-svg-icons/faFolderOpen'
import FormNumberField from '../../../../client/js/components/form/fields/FormNumberField'
import { AuthContext } from '../../../auth/components/AuthProvider'

export type InvoiceDetailsItemProps = {
  index: number,
  locked: boolean,
  name: string,
  onRemove (): void
}

function InvoiceDetailsItem (props: InvoiceDetailsItemProps) {
  const intl = useIntl()
  const { index, locked, name, onRemove } = props
  const { disabled, getValue, setValues, values } = useFormContext()
  const { hasPermission } = useContext(AuthContext)
  const item = values.Transactions[index]
  const categoryOptions = useTransactionCategoryOptions()

  const isDisabled = useMemo<boolean>(() => (
    disabled || item.generated || (values.paid && locked)
  ), [disabled, item.generated, locked, values.paid])

  const [isInterventionModalOpen, setIsInterventionModalOpen] = useState(false)

  const toggleInterventionModal = useCallback(() => {
    setIsInterventionModalOpen((s) => !s)
  }, [])

  const handleRemoveIntervention = useCallback(() => {
    setValues({ [`${name}.interventionId`]: null }, { partial: true })
  }, [name, setValues])

  const handleSelectIntervention = useCallback((intervention: Intervention) => {
    setValues({
      [`${name}.interventionId`]: intervention.id,
      [`${name}.label`]: intervention.subject
    }, { partial: true })
    toggleInterventionModal()
  }, [name, setValues, toggleInterventionModal])

  const interventionFilter: Record<string, any> = useMemo(() => ({
    estateId: values.estateId,
    leaseId: values.leaseId,
    missingTransaction: true
  }), [values.estateId, values.leaseId])

  return (
    <>
      <InterventionSelectorModal
        queryFilter={interventionFilter}
        onClickResult={handleSelectIntervention}
        onClose={toggleInterventionModal}
        opened={isInterventionModalOpen}
      />

      <Table.Tr>
        <Table.Td>
          <Group gap="xs" wrap="nowrap">
            <ActionIcon
              disabled={isDisabled && values.id && values.paid}
              onClick={onRemove}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </ActionIcon>
            <ActionIcon
              disabled={!item.id}
              rel="noopener"
              component={AnchorLink}
              target="_blank"
              to={getTransactionLink(item.id, item.expense)}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faLink}
              />
            </ActionIcon>
            {!item.generated
              ? <Tooltip
                label={intl.formatMessage({
                  id: 'manual_transaction_info',
                  defaultMessage: 'Transaction ajoutée manuellement'
                })}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faInfoCircle}
                />
              </Tooltip>
              : null}
          </Group>
        </Table.Td>
        <Table.Td style={{ verticalAlign: 'middle' }}>
          <FormField
            disabled={disabled || values.paid || values[`${name}.paid`] || !hasPermission('change_paid:invoice')}
            name={`${name}.paid`}
            parser={parseBoolean}
            render={Checkbox}
            type="checkbox"
            value="true"
          />
        </Table.Td>
        <Table.Td>
          <FormField
            disabled={isDisabled}
            name={`${name}.label`}
            parser={upperFirst}
            placeholder={intl.formatMessage({
              id: 'label',
              defaultMessage: 'Libellé'
            })}
            rightSection={getValue(`${name}.interventionId`)
              ? <ActionIcon
                disabled={isDisabled}
                onClick={handleRemoveIntervention}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faTimesCircle}
                />
              </ActionIcon>
              : <ActionIcon
                disabled={isDisabled}
                onClick={toggleInterventionModal}
                title={intl.formatMessage({
                  id: 'intervention',
                  defaultMessage: 'Intervention'
                })}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faFolderOpen}
                />
              </ActionIcon>}
          />
        </Table.Td>
        <Table.Td>
          <FormField
            disabled={isDisabled}
            name={`${name}.category`}
            render={Select}
            data={[
              ...categoryOptions.map(({ label, value }) => ({
                value: String(value),
                label
              }))
            ]}
          />
        </Table.Td>
        <Table.Td>
          <FormNumberField
            disabled={isDisabled}
            name={`${name}.amount`}
            parser={parseNumber}
            placeholder={intl.formatMessage({
              id: 'amount_excluding_tax',
              defaultMessage: 'Montant HT'
            })}
          />
        </Table.Td>
        <Table.Td>
          <FormNumberField
            disabled={isDisabled}
            name={`${name}.vat`}
            parser={parseNumber}
            placeholder={intl.formatMessage({
              id: 'vat',
              defaultMessage: 'TVA'
            })}
          />
        </Table.Td>
        <Table.Td>
          <FormNumberField
            disabled={isDisabled}
            name={`${name}.cps`}
            parser={parseNumber}
            placeholder={intl.formatMessage({
              id: 'cps',
              defaultMessage: 'CPS'
            })}
          />
        </Table.Td>
      </Table.Tr>
    </>
  )
}

export default InvoiceDetailsItem
